<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-summary">
        <i class="far fa-dolly"></i> Production Despatch Performance
      </Tab>
      <Tab tabid="tab-panel-list">
        <i class="far fa-list"></i> Production Despatch Panels
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-summary">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Production Despatch Summary For <span v-if="!appliedSummaryFilter.contactId">All Contacts</span><span v-if="appliedSummaryFilter.contactId">{{appliedSummaryFilter.contactName}}</span></ContentTitle>
            <ContentSubTitle>Showing panels due or despatched between {{appliedSummaryFilter.startDate | formatDate}} and {{appliedSummaryFilter.endDate | formatDate}}</ContentSubTitle>
          </div>
          <div class="col-lg-6">
            <ReportFilterPopover
              class="float-right"
              headerTitle="Despatch Performance Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applySummaryFilter"
              @reset="resetSummaryFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Date Range">
                  <ReportDateRangeSelect
                    :startDate="editingSummaryFilter.startDate"
                    :endDate="editingSummaryFilter.endDate"
                    @updateStartDate="editingSummaryFilter.startDate = $event"
                    @updateEndDate="editingSummaryFilter.endDate = $event"
                  />
                </ReportFilterGroup>
                <ReportFilterGroup label="Contact">
                  <ReportContactSelect
                    v-model="editingSummaryFilter.contactId"
                    @updateName="editingSummaryFilter.contactName = $event"
                  />
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedSummaryFilter.startDate | formatDate}} - {{appliedSummaryFilter.endDate | formatDate}}
                </div>
                <div v-if="appliedSummaryFilter.contactId">
                  <i class="far fa-user-circle mr-2"></i>{{appliedSummaryFilter.contactName}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-lg-12">
            <ContentBox>
              <template v-slot:content>
                <div class="row no-gutter">
                  <div class="col-3">
                    <template v-if="despatchSummary.totalDaysBehind >= 0">
                      <ContentTitle>Total Days Average</ContentTitle>
                      <ContentSubTitle>Average days behind/ahead despatch due date</ContentSubTitle>
                      <h1 v-html="displayDaysBehindAvg(despatchSummary.totalDaysBehindAvg)" />
                    </template>
                    <template v-if="despatchSummary.totalDaysBehind < 0">
                      <ContentTitle>Total Days Average</ContentTitle>
                      <h1>{{despatchSummary.totalDaysAhead | formatInteger(false, true)}}</h1>
                    </template>
                  </div>
                  <div class="col-3">
                    <ContentTitle>Overdue Panels Pending Production</ContentTitle>
                    <ContentSubTitle>Overdue or due today</ContentSubTitle>
                    <h1>{{despatchSummary.totalPanelsPendingProduction | formatInteger(false, true)}}</h1>
                  </div>
                  <div class="col-3">
                    <ContentTitle>Panels Pending Despatch</ContentTitle>
                    <ContentSubTitle>Overdue or due today</ContentSubTitle>
                    <h1>{{despatchSummary.totalPanelsPendingDespatch | formatInteger(false, true)}}</h1>
                  </div>
                  <div class="col-3">
                    <ContentTitle>Panels Despatched</ContentTitle>
                    <ContentSubTitle>Panels despatched during the date range</ContentSubTitle>
                    <h1>{{despatchSummary.totalPanelsCompletedDespatch | formatInteger(false, true)}}</h1>
                  </div>
                </div>
              
              </template>
            </ContentBox>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12">
            <ButtonGroup class="float-right" :buttons="[{title: 'Values', icon: 'fa-list-ol'}, {title: 'Percentages', icon: 'fa-percent'}]" sizeClass="btn-xs" v-model="displayValueMode" />
            <ContentTitle>Despatch Type Breakdown</ContentTitle>
            <ContentSubTitle>Based on panels completed, due or overdue during this date range</ContentSubTitle>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list" striped hover>
              <template v-slot:header>
                <tr>
                  <th> </th>
                  <th colspan="3" class="text-center">Standard Lead Time</th>
                  <th colspan="3" class="text-center table-data-alternate-column">Requested Earlier Lead Time</th>
                </tr>
                <tr>
                  <th>Despatch Type</th>

                  <th>Panels</th>
                  <th>Total Days Average Behind Due Date</th>
                  <th>Total Days Average Behind Despatch Lead Time</th>

                  <th class="table-data-alternate-column">Panels</th>
                  <th class="table-data-alternate-column">Total Days Average Behind Due Date</th>
                  <th class="table-data-alternate-column">Total Days Average Behind Despatch Lead Time</th>
                </tr>
              </template>
              <template v-slot:body>
                <template v-for="(despatch, index) in despatchList">
                  <tr :key="`despatch-type-${index}`">
                    <td class="font-weight-bold">
                      {{despatch.name}}
                    </td>

                    <td class="text-center">{{despatch.normalStats.totalPanels}}</td>
                    <td class="text-center">
                      <span v-html="displayDaysBehindAvg(despatch.normalStats.totalDaysBehindDueAvg)" />
                    </td>
                    <td class="text-center">
                      <span v-html="displayDaysBehindAvg(despatch.normalStats.totalDaysBehindAvg)" />
                    </td>

                    <td class="text-center table-data-alternate-column">{{despatch.requestedEarlierStats.totalPanels}}</td>
                    <td class="text-center table-data-alternate-column">
                      <span v-html="displayDaysBehindAvg(despatch.requestedEarlierStats.totalDaysBehindDueAvg)" />
                    </td>
                    <td class="text-center table-data-alternate-column">
                      <span v-html="displayDaysBehindAvg(despatch.requestedEarlierStats.totalDaysBehindAvg)" />
                    </td>
                    
                  </tr>
                </template>

                <tr class="table-totals-row-header font-weight-bold">
                  <td>Totals</td>
                  <td class="text-center">{{totalNormalPanelCount | formatInteger(false, true)}}</td>
                  <td colspan="2"> </td>
                  <td class="text-center">{{totalEarlierPanelCount | formatInteger(false, true)}}</td>
                  <td colspan="2"> </td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>


      <TabPage tabid="tab-panel-list">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Production Panel List For <span v-if="!appliedSummaryFilter.contactId">All Contacts</span><span v-if="appliedSummaryFilter.contactId">{{appliedSummaryFilter.contactName}}</span></ContentTitle>
            <ContentSubTitle>Showing panels due or despatched between {{appliedSummaryFilter.startDate | formatDate}} and {{appliedSummaryFilter.endDate | formatDate}}</ContentSubTitle>
          </div>
          <div class="col-lg-6">
            <ReportFilterPopover
              class="float-right"
              headerTitle="Despatch Performance Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applySummaryFilter"
              @reset="resetSummaryFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Date Range">
                  <ReportDateRangeSelect
                    :startDate="editingSummaryFilter.startDate"
                    :endDate="editingSummaryFilter.endDate"
                    @updateStartDate="editingSummaryFilter.startDate = $event"
                    @updateEndDate="editingSummaryFilter.endDate = $event"
                  />
                </ReportFilterGroup>
                <ReportFilterGroup label="Contact">
                  <ReportContactSelect
                    v-model="editingSummaryFilter.contactId"
                    @updateName="editingSummaryFilter.contactName = $event"
                  />
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedSummaryFilter.startDate | formatDate}} - {{appliedSummaryFilter.endDate | formatDate}}
                </div>
                <div v-if="appliedSummaryFilter.contactId">
                  <i class="far fa-user-circle mr-2"></i>{{appliedSummaryFilter.contactName}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list" striped hover>
              <template v-slot:header>
                <tr>
                  <th>Panel</th>
                  <th>Panel Status</th>
                  <th>Panel Size</th>
                  <th>Order</th>
                  <th>Order Status</th>
                  <th>Despatch Type</th>
                  <th>Production Due</th>
                  <th>Despatch Due</th>
                  <th>Production Completed</th>
                  <th>Despatch Completed</th>
                  <th class="text-center">Order Requested Earlier</th>
                  <th class="text-center">Days Behind Due Date</th>
                  <th class="text-center">Days Behind Despatch Lead Time</th>
                </tr>
              </template>
              <template v-slot:body>
                <template v-for="(panel, index) in despatchSummary.productionPanels">
                  <tr :key="`despatch-type-${index}`">
                    <td class="font-weight-bold">
                      {{panel.barcode}}
                    </td>
                    <td>
                      <LabelPanelStatusBadge :status="panel.status" />
                    </td>
                    <td>
                      {{panel.panelHeight}}x{{panel.panelWidth}}
                    </td>
                    <td>
                      <LinkOrder :id="panel.order.id" :name="panel.order.sequenceNumber" />
                    </td>
                    <td>
                      <LabelOrderStatusBadge :status="panel.order.status" />
                    </td>
                    <td>
                      <LabelBadge labelClass="label-despatch-badge">{{panel.order.despatchTaskType ? panel.order.despatchTaskType.name : ''}}</LabelBadge>
                    </td>
                    <td>
                      {{panel.dateProductionDue | formatDate | formatHyphenEmptyValue}}
                    </td>
                    <td>
                      {{panel.dateDespatchDue | formatDate | formatHyphenEmptyValue}}
                    </td>
                    <td>
                      {{panel.dateProductionCompleted | formatDate | formatHyphenEmptyValue}}
                    </td>
                    <td>
                      {{panel.dateCompleted | formatDate | formatHyphenEmptyValue}}
                    </td>
                    <td class="text-center">
                      <IconBoolean :value="panel.isRequestedEarlier" :displayFalse="false" />
                    </td>
                    <td class="text-center">
                      <span v-html="displayDaysBehindAvg(panel.despatchDaysBehindDue)" />
                    </td>
                    <td class="text-center">
                      <span v-html="displayDaysBehindAvg(panel.despatchDaysBehind)" />
                    </td>
                  </tr>  
                </template>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>
      
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'
import { mapState } from 'vuex'

import Utils from '@/utils/utils'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportContactSelect from '@/components/reporting/reportContactSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import LabelPanelStatusBadge from '@/modules/production/components/labelPanelStatusBadge'
import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'

export default {
  name: 'ProductionPerformance',
  components: {
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportContactSelect,
    ReportFilterGroup,
    LabelPanelStatusBadge,
    LabelOrderStatusBadge
  },
  data() {
    return {
      loading: false,
      displayValueMode: 0,
      despatchSummary: {},
      
      defaultSummaryFilter: {    
        startDate: moment().local().startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        contactId: null,
        contactName: '',
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {}
    }
  },
  computed: {
    despatchList() {
      if(!this.despatchSummary.despatchTaskTypes)
        return []
      return this.despatchSummary.despatchTaskTypes
    },
    totalNormalPanelCount() {
      return this.despatchList.reduce((curValue, despatch) => curValue + despatch.normalStats.totalPanels, 0)
    },
    totalEarlierPanelCount() {
      return this.despatchList.reduce((curValue, despatch) => curValue + despatch.requestedEarlierStats.totalPanels, 0)
    },
    
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.requestSummary()
  },
  beforeDestroy() {
  },
  methods: {
    requestSummary() {
      let startDate = moment(this.appliedSummaryFilter.startDate).local().startOf('day').toDate().toISOString()
      let endDate = moment(this.appliedSummaryFilter.endDate).local().endOf('day').toDate().toISOString()

      let filterData = {
        startDate: startDate,
        endDate: endDate,
        contactId: this.appliedSummaryFilter.contactId,
        includePanels: true
      }

      this.loading = true
      this.$apiService.analyticsService.getProductionDespatchSummary(filterData)
      .then(response => {
        this.despatchSummary = response.data
      })
      .finally(() => {
        this.loading = false
      })
    },
    displayDaysBehindAvg(average) {
      var displayClass = ''
      var symbol = ''
      var outputAvg = this.$options.filters.formatDecimal(average, 2, true)

      if(average > 0) {
        displayClass = 'stats-bad'
        symbol = '+'
      } else if(average < 0) {
        average = -average
        displayClass = 'stats-good'
      }

      return `<span class="${displayClass}">${symbol}${outputAvg}</span>`
    },
    displayDaysBehind(days) {
      let outputDays = this.$options.filters.formatInteger(days, false, true)
      if(days > 0)
        return `+${outputDays}`
      else if(days < 0)
        return `-${outputDays}`
      return outputDays
    },
    displayPanelCount(panelCount, totalPanelCount) {
      if(this.displayValueMode == 0)
        return this.$options.filters.formatInteger(panelCount, false, true)

      var percent = 0
      if(totalPanelCount > 0)
        percent = panelCount / totalPanelCount * 100.0

      return this.$options.filters.formatPercent(percent)
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestSummary()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestSummary()
    }

  }
}
</script>
